import { isEmpty, isNil } from "ramda";
import { isNullOrUndefinedDeep } from ".";
import { FIELD_TYPES } from "./FieldTypes";
import { normalizeValue } from "./FieldTypes.normalize";
export function toJsonSchema(fieldType) {
    const fieldDef = FIELD_TYPES[fieldType];
    if (!fieldDef || !fieldDef.parts) {
        return null;
    }
    const parts = fieldDef.parts;
    // Simple types: "string", "number", "boolean"
    if (parts === "string") {
        return { type: "string" };
    }
    if (parts === "number") {
        return { type: "number" };
    }
    if (parts === "boolean") {
        return { type: "boolean" };
    }
    // Array type: "string[]"
    if (parts === "string[]") {
        return { type: "array", items: { type: "string" } };
    }
    // Object type: Record<string, string> like { "firstName": "string", "day": "number" }
    if (typeof parts === "object") {
        const properties = {};
        for (const [key, valueType] of Object.entries(parts)) {
            if (valueType === "string") {
                properties[key] = { type: "string" };
            }
            else if (valueType === "number") {
                properties[key] = { type: "number" };
            }
            else if (valueType === "boolean") {
                properties[key] = { type: "boolean" };
            }
        }
        return { type: "object", properties };
    }
    return null;
}
export function validateFieldType(fieldDef, fieldPath, value) {
    if (!fieldDef || !fieldDef.type) {
        throw new Error(`Field type ${fieldDef.type} is not defined`);
    }
    const normalizedValue = normalizeValue(value, fieldDef);
    // If normalization returned null, the value couldn't be converted
    if (fieldDef.required && normalizedValue === null) {
        return {
            fieldPath: fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' is required but was not provided. Please enter the value.`,
        };
    }
    else if (!fieldDef.required && isNullOrUndefinedDeep(value)) {
        return {
            fieldPath: fieldPath,
            type: "correct",
            question: ``,
        };
    }
    else if (isNil(normalizedValue)) {
        const hint = FIELD_TYPES[fieldDef.type]?.shortDescription
            ? ` Expected: ${FIELD_TYPES[fieldDef.type].shortDescription}`
            : "";
        return {
            fieldPath: fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' is invalid.${hint}`,
        };
    }
    switch (fieldDef.type) {
        case "text":
            return validateText(fieldDef, fieldPath, normalizedValue);
        case "classification":
            return validateCorrect(fieldPath, normalizedValue);
        case "scheduling":
            return validateCorrect(fieldPath, normalizedValue);
        case "options":
            return validateOptions(fieldDef, fieldPath, normalizedValue);
        case "multipleOptions":
            return validateMultipleOptions(fieldDef, fieldPath, normalizedValue);
        case "yesno":
            return validateYesNo(fieldPath, normalizedValue);
        case "fullName":
            return validateFullName(fieldDef, fieldPath, normalizedValue);
        case "email":
            return validateEmail(fieldDef, fieldPath, normalizedValue);
        case "date":
            return validateDate(fieldDef, fieldPath, normalizedValue);
        case "address":
            return validateAddress(fieldDef, fieldPath, normalizedValue);
        case "phone":
            return validatePhone(fieldDef, fieldPath, normalizedValue);
        case "composedList":
            return validateCorrect(fieldPath, normalizedValue);
        case "composed":
            return validateCorrect(fieldPath, normalizedValue);
        case "age":
            return validateAge(fieldDef, fieldPath, normalizedValue);
        case "timeOfDay":
            return validateTimeOfDay(fieldDef, fieldPath, normalizedValue);
        case "duration":
            return validateCorrect(fieldPath, normalizedValue);
        case "number":
            return validateCorrect(fieldPath, normalizedValue);
        case "integer":
            return validateCorrect(fieldPath, normalizedValue);
        // case "money":
        //   return validateMoney(fieldDef, fieldPath, normalizedValue);
        case "percentage":
            return validateCorrect(fieldPath, normalizedValue);
        case "url":
            return validateUrl(fieldDef, fieldPath, normalizedValue);
        case "file":
            return validateFile(fieldDef, fieldPath, normalizedValue);
        default:
            throw new Error(`Field type ${fieldDef.type} is not defined`);
    }
}
// Helper for simple validated types that just need to return correct
function validateCorrect(fieldPath, value) {
    return {
        fieldPath,
        type: "correct",
        question: "",
        inferredData: value,
    };
}
function validateText(fieldDef, fieldPath, value) {
    return validateCorrect(fieldPath, value);
}
function validateOptions(fieldDef, fieldPath, value) {
    const options = fieldDef.options;
    const validChoice = options?.find((option) => option.value === value);
    if (!validChoice) {
        return {
            fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' must be one of: ${options?.map(o => o.value).join(", ")}`,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateMultipleOptions(fieldDef, fieldPath, value) {
    const options = fieldDef.options?.map((option) => option.value);
    const invalidItems = value.filter((item) => !options?.includes(item));
    if (invalidItems.length > 0) {
        return {
            fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' contains invalid options: ${invalidItems.join(", ")}`,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateYesNo(fieldPath, value) {
    return validateCorrect(fieldPath, value);
}
function validateFullName(fieldDef, fieldPath, value) {
    const missingParts = [];
    if (isEmpty(value.firstName.trim())) {
        missingParts.push("firstName");
    }
    if (isEmpty(value.lastName.trim())) {
        missingParts.push("lastName");
    }
    if (missingParts.length > 0) {
        return {
            fieldPath,
            type: "missing-part",
            question: `Field '${fieldDef.label || fieldDef.key}' is missing: ${missingParts.join(", ")}`,
            missingParts,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateEmail(fieldDef, fieldPath, value) {
    // RFC 5322 compliant email regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(value)) {
        return {
            fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' must be a valid email address`,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateDate(fieldDef, fieldPath, value) {
    const missingParts = [];
    if (value.day < 1 || value.day > 31) {
        missingParts.push("day");
    }
    if (value.month < 1 || value.month > 12) {
        missingParts.push("month");
    }
    if (value.year < 1) {
        missingParts.push("year");
    }
    if (missingParts.length > 0) {
        return {
            fieldPath,
            type: "missing-part",
            question: `Field '${fieldDef.label || fieldDef.key}' has invalid: ${missingParts.join(", ")}`,
            missingParts,
            inferredData: value,
        };
    }
    // Validate the date is actually valid (e.g., Feb 30 is not valid)
    const testDate = new Date(value.year, value.month - 1, value.day);
    if (testDate.getDate() !== value.day || testDate.getMonth() !== value.month - 1 || testDate.getFullYear() !== value.year) {
        return {
            fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' contains an invalid date`,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateAddress(fieldDef, fieldPath, value) {
    const missingParts = [];
    if (isEmpty(value.addressLine1.trim())) {
        missingParts.push("addressLine1");
    }
    if (isEmpty(value.city.trim())) {
        missingParts.push("city");
    }
    if (missingParts.length > 0) {
        return {
            fieldPath,
            type: "missing-part",
            question: `Field '${fieldDef.label || fieldDef.key}' is missing: ${missingParts.join(", ")}`,
            missingParts,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
const PHONE_PART_MESSAGES = {
    countryCode: {
        missing: "Please provide a country code (1–3 digits, e.g. \"1\" for US or \"44\" for UK).",
        invalid: "The country code must be 1–3 digits (e.g. \"1\", \"44\", \"351\").",
    },
    areaCode: {
        missing: "Please provide an area code.",
        invalid: "The area code must contain digits only (e.g. \"415\", \"800\").",
    },
    number: {
        missing: "Please provide a phone number.",
        invalid: "The phone number must contain digits only (e.g. \"5551234\", \"2991133\").",
    },
};
function validatePhone(fieldDef, fieldPath, value) {
    const label = fieldDef.label || fieldDef.key;
    const missingParts = [];
    const invalidParts = [];
    if (!value.countryCode || isEmpty(value.countryCode.trim())) {
        missingParts.push("countryCode");
    }
    else if (!/^\d{1,3}$/.test(value.countryCode.trim())) {
        invalidParts.push("countryCode");
    }
    if (value.areaCode !== undefined && !/^\d+$/.test(value.areaCode.trim())) {
        invalidParts.push("areaCode");
    }
    if (!value.number || isEmpty(value.number.trim())) {
        missingParts.push("number");
    }
    else if (!/^\d+$/.test(value.number.trim())) {
        invalidParts.push("number");
    }
    if (missingParts.length > 0) {
        const hints = missingParts.map(p => PHONE_PART_MESSAGES[p]?.missing).filter(Boolean).join(" ");
        return {
            fieldPath,
            type: "missing-part",
            question: `'${label}' is incomplete. ${hints}`,
            missingParts,
            inferredData: value,
        };
    }
    if (invalidParts.length > 0) {
        const hints = invalidParts.map(p => PHONE_PART_MESSAGES[p]?.invalid).filter(Boolean).join(" ");
        return {
            fieldPath,
            type: "retry",
            question: `'${label}' has an invalid value. ${hints}`,
            invalidParts,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateAge(fieldDef, fieldPath, value) {
    if (value < 0 || value > 150) {
        return {
            fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' must be a valid age (0-150)`,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateTimeOfDay(fieldDef, fieldPath, value) {
    const missingParts = [];
    if (value.hour < 0 || value.hour > 23) {
        missingParts.push("hour");
    }
    if (value.minute < 0 || value.minute > 59) {
        missingParts.push("minute");
    }
    if (missingParts.length > 0) {
        return {
            fieldPath,
            type: "missing-part",
            question: `Field '${fieldDef.label || fieldDef.key}' has invalid: ${missingParts.join(", ")}`,
            missingParts,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
// The money field is not being used as of now.
// function validateMoney(fieldDef: FieldConfig, fieldPath: string, value: MoneyValue): FieldValidationResult {
//   // ISO 4217 currency code regex (3 uppercase letters)
//   const currencyRegex = /^[A-Z]{3}$/;
//   if (!currencyRegex.test(value.currency)) {
//     return {
//       fieldPath,
//       type: "retry",
//       question: `Field '${fieldDef.label || fieldDef.key}' must have a valid currency code (e.g., USD, EUR)`,
//       inferredData: value,
//     };
//   }
//   return validateCorrect(fieldPath, value);
// }
function validateUrl(fieldDef, fieldPath, value) {
    // Domain regex: allows subdomains, domains, and TLDs
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;
    if (!domainRegex.test(value.domain)) {
        return {
            fieldPath,
            type: "retry",
            question: `Field '${fieldDef.label || fieldDef.key}' must have a valid domain`,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
function validateFile(fieldDef, fieldPath, value) {
    const missingParts = [];
    if (isEmpty(value.fileName.trim())) {
        missingParts.push("fileName");
    }
    if (isEmpty(value.fileUri.trim())) {
        missingParts.push("fileUri");
    }
    if (missingParts.length > 0) {
        return {
            fieldPath,
            type: "missing-part",
            question: `Field '${fieldDef.label || fieldDef.key}' is missing: ${missingParts.join(", ")}`,
            missingParts,
            inferredData: value,
        };
    }
    return validateCorrect(fieldPath, value);
}
